import React, { useState, useEffect } from 'react';
import { auth } from './firebase';
import { onAuthStateChanged } from 'firebase/auth';
import { db } from './firebase';
import { doc, onSnapshot } from 'firebase/firestore';
import ErrorBoundary from './ErrorBoundary';
import Login from './components/Login';
import Home from './components/Home';
import Privacy from './components/Privacy';
import './App.css';

// ─── TIMEZONE-SAFE DATE HELPER ────────────────────────────────────────────────
function getLocalDateString() {
  const now = new Date();
  const y   = now.getFullYear();
  const m   = String(now.getMonth() + 1).padStart(2, '0');
  const d   = String(now.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

function App() {
  const [user,        setUser]        = useState(null);
  const [loading,     setLoading]     = useState(true);
  const [maintenance, setMaintenance] = useState(false);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (currentUser) => {
      setUser(currentUser);
      setLoading(false);
    });
    return unsubscribe;
  }, []);

  useEffect(() => {
    const unsub = onSnapshot(doc(db, 'config', 'maintenance'), (snap) => {
      setMaintenance(snap.exists() && snap.data().enabled === true);
    });
    return unsub;
  }, []);

  // ── Public pages ──────────────────────────────────────────────────────────
  const path = window.location.pathname;
  if (path === '/privacy') return <Privacy />;
  if (path === '/delete-account') return <DeleteAccount />;
  if (path === '/delete-data') return <DeleteData />;

  if (maintenance) {
    return (
      <div style={{ minHeight:'100vh', backgroundColor:'#0B1928', display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'center', padding:'24px', fontFamily:"'Inter',-apple-system,sans-serif", textAlign:'center' }}>
        <div style={{ fontSize:'48px', marginBottom:'24px' }}>🏆</div>
        <div style={{ fontSize:'32px', fontWeight:'900', color:'#FF6B00', letterSpacing:'6px', marginBottom:'12px' }}>BRACKETZ</div>
        <div style={{ fontSize:'13px', color:'rgba(255,255,255,0.4)', letterSpacing:'3px', marginBottom:'40px', textTransform:'uppercase' }}>Daily Bracket Challenge</div>
        <div style={{ width:'48px', height:'2px', backgroundColor:'#FF6B00', marginBottom:'32px' }}></div>
        <div style={{ fontSize:'20px', fontWeight:'700', color:'#F0F4F8', marginBottom:'12px' }}>We will be right back</div>
        <div style={{ fontSize:'14px', color:'rgba(255,255,255,0.5)', lineHeight:'1.6', maxWidth:'280px' }}>Bracketz is currently undergoing scheduled maintenance. Check back soon.</div>
      </div>
    );
  }

  if (loading) {
    return (
      <div style={{
        display:         'flex',
        flexDirection:   'column',
        alignItems:      'center',
        justifyContent:  'center',
        minHeight:       '100vh',
        backgroundColor: '#0B1928',
      }}>
        <div style={{
          fontSize:      '32px',
          fontWeight:    '900',
          color:         '#FF6B00',
          letterSpacing: '8px',
          fontFamily:    'Inter, Arial, sans-serif',
        }}>
          BRACKETZ
        </div>
      </div>
    );
  }

  return (
    <ErrorBoundary>
      <div className="App">
        {user ? <Home user={user} /> : <Login />}
      </div>
    </ErrorBoundary>
  );
}

// ── Simple delete account page ────────────────────────────────────────────────
function DeleteAccount() {
  const font = `'Inter',-apple-system,BlinkMacSystemFont,'Segoe UI',sans-serif`;
  return (
    <div style={{ maxWidth:'680px', margin:'0 auto', minHeight:'100vh', backgroundColor:'#0B1928', fontFamily:font, padding:'40px 24px 80px' }}>
      <div style={{ fontSize:'13px', fontWeight:'700', color:'#FF6B00', letterSpacing:'5px', marginBottom:'16px' }}>BRACKETZ</div>
      <h1 style={{ fontSize:'28px', fontWeight:'700', color:'#F0F4F8', marginBottom:'8px' }}>Delete Your Account</h1>
      <p style={{ fontSize:'14px', color:'#8496A8', lineHeight:'1.8', marginBottom:'24px' }}>
        To delete your Bracketz account and all associated data, follow these steps:
      </p>
      <ol style={{ fontSize:'14px', color:'#8496A8', lineHeight:'2', paddingLeft:'20px' }}>
        <li>Open the Bracketz app</li>
        <li>Tap your profile icon in the bottom navigation</li>
        <li>Scroll down to Settings</li>
        <li>Tap "Delete Account"</li>
        <li>Confirm deletion</li>
      </ol>
      <p style={{ fontSize:'14px', color:'#8496A8', lineHeight:'1.8', marginTop:'24px' }}>
        Your account and all data will be permanently deleted within 30 days. For assistance, contact us at <span style={{ color:'#FF6B00' }}>privacy@playbracketz.com</span>
      </p>
    </div>
  );
}

// ── Simple delete data page ───────────────────────────────────────────────────
function DeleteData() {
  const font = `'Inter',-apple-system,BlinkMacSystemFont,'Segoe UI',sans-serif`;
  return (
    <div style={{ maxWidth:'680px', margin:'0 auto', minHeight:'100vh', backgroundColor:'#0B1928', fontFamily:font, padding:'40px 24px 80px' }}>
      <div style={{ fontSize:'13px', fontWeight:'700', color:'#FF6B00', letterSpacing:'5px', marginBottom:'16px' }}>BRACKETZ</div>
      <h1 style={{ fontSize:'28px', fontWeight:'700', color:'#F0F4F8', marginBottom:'8px' }}>Delete Your Data</h1>
      <p style={{ fontSize:'14px', color:'#8496A8', lineHeight:'1.8', marginBottom:'24px' }}>
        You can request deletion of specific data without deleting your account.
      </p>
      <p style={{ fontSize:'14px', color:'#8496A8', lineHeight:'1.8', marginBottom:'16px' }}>
        <strong style={{ color:'#F0F4F8' }}>Data we can delete on request:</strong>
      </p>
      <ul style={{ fontSize:'14px', color:'#8496A8', lineHeight:'2', paddingLeft:'20px' }}>
        <li>Bracket pick history</li>
        <li>Comments and messages</li>
        <li>Friend connections</li>
        <li>Notification preferences</li>
      </ul>
      <p style={{ fontSize:'14px', color:'#8496A8', lineHeight:'1.8', marginTop:'24px' }}>
        To request data deletion, email us at <span style={{ color:'#FF6B00' }}>privacy@playbracketz.com</span> with your account email address. We will process your request within 30 days.
      </p>
    </div>
  );
}

export default App;
